import { useState, useCallback, useMemo } from "react";
import { Separator, DateRangePicker, Button, useToast } from "@racoon/ui";
import { Namespace, useLang, useT } from "@racoon/i18n";
import { useLocation } from "react-router-dom";
import {
  useGetGovGeoAccessQuery,
  useGetGovAnalyticsAnomaliesForAllQuery,
  useGetGovAnalyticsDisposalVisitsForAllQuery,
  useTrucksQuery,
} from "~/shared/api/govDash/govDash.api";
import { useGetDailySummaryListQuery } from "~/shared/api/daily-summary/daily-summary.api";
import { useDriverOnShiftsInRange } from "~/shared/hooks/use-active-truck-count.hook";
import { MVODetailedView } from "./mvo-detailed-view";
import { IcDownload } from "@racoon/icons";
import type { AnalyticsPageReturnState } from "./mvo-analytics-section.types";
import { DropdownSelector } from "~/ui/components/dropdownSelector";
import {
  endOfLocalDay,
  isSameLocalDay,
  normalizeDateRange,
  parseLocalDateParam,
  startOfLocalDay,
  toDateParam,
} from "~/shared/utils/filterDateFormatter";
import { AllMvosAnalyticsOverview, type OverviewViewId } from "./all-mvos-analytics-overview";
import type { ConnectProto } from "@racoon/api";
import { aggregateSummaryByOrganization } from "./analytics-report.shared";

interface FilterValue {
  label: string;
  value: string;
}

type DateRange = {
  from?: Date;
  to?: Date;
};

export function AnalyticsPage() {
  const { lang } = useLang();
  const t = useT();
  const { toast } = useToast();
  const location = useLocation();
  const returnState = (location.state ?? null) as AnalyticsPageReturnState | null;
  const todayStart = useMemo(() => startOfLocalDay(new Date()), []);
  const initialDateRange = useMemo<DateRange>(() => {
    const from =
      parseLocalDateParam(returnState?.dateFrom) ??
      parseLocalDateParam(returnState?.dailyDate) ??
      parseLocalDateParam(returnState?.periodFrom) ??
      todayStart;
    const to =
      parseLocalDateParam(returnState?.dateTo) ??
      parseLocalDateParam(returnState?.dailyDate) ??
      parseLocalDateParam(returnState?.periodTo) ??
      from;
    return normalizeDateRange({ from, to });
  }, [returnState, todayStart]);
  const [selectedRegion] = useState<FilterValue>({
    label:
      returnState?.selectedRegion?.label ?? t(Namespace.Govdash, "allDistricts") ?? "Все районы",
    value: returnState?.selectedRegion?.value ?? "all",
  });
  const { data: { govGeoAccesses: geoAccessData } = {} } = useGetGovGeoAccessQuery({});
  const districtOrganizationIds = useMemo(
    () => (geoAccessData ?? []).map((a) => a.organizationId),
    [geoAccessData],
  );

  const [selectedMvo, setSelectedMvo] = useState<FilterValue>({
    label: returnState?.selectedMvo?.label ?? t(Namespace.Govdash, "allMvos") ?? "Все МВО",
    value: returnState?.selectedMvo?.value ?? "all",
  });

  const {
    data: truckData,
    isLoading: isTruckDataLoading,
    isFetching: isTruckDataFetching,
  } = useTrucksQuery(
    {
      districtOrganizationIds,
      serviceProviderIds: selectedMvo.value !== "all" ? [BigInt(selectedMvo.value)] : [],
    },
    { skip: selectedMvo.value === "all" },
  );

  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
  // Which all-MVOs overview tab is open. Lives here because the PDF export only
  // covers the visible tab.
  const [allMvosView, setAllMvosView] = useState<OverviewViewId>("landfills");
  const activeStartDate = dateRange.from ?? todayStart;
  const activeEndDate = endOfLocalDay(dateRange.to ?? activeStartDate);
  const isSingleDay = isSameLocalDay(activeStartDate, activeEndDate);

  const normalizedSelectedTopDate = startOfLocalDay(activeEndDate);
  const progressDate = normalizedSelectedTopDate;
  const exportProgressStartDate = isSingleDay ? progressDate : activeStartDate;
  const exportProgressEndDate = isSingleDay ? progressDate : activeEndDate;
  const exportChartStartDate = activeStartDate;
  const exportChartEndDate = activeEndDate;
  const progressSummaryStartDate =
    selectedMvo.value === "all" ? activeStartDate : exportProgressStartDate;
  const progressSummaryEndDate =
    selectedMvo.value === "all" ? activeEndDate : exportProgressEndDate;

  const {
    data: progressSummaryData,
    isLoading: isProgressSummaryLoading,
    isFetching: isProgressSummaryFetching,
  } = useGetDailySummaryListQuery(
    {
      owner_organization_id: selectedMvo.value !== "all" ? selectedMvo.value : undefined,
      date_from: progressSummaryStartDate ? toDateParam(progressSummaryStartDate) : undefined,
      date_to: progressSummaryEndDate ? toDateParam(progressSummaryEndDate) : undefined,
    },
    { skip: !progressSummaryStartDate || !progressSummaryEndDate },
  );

  const {
    data: chartSummaryData,
    isLoading: isChartSummaryLoading,
    isFetching: isChartSummaryFetching,
  } = useGetDailySummaryListQuery(
    {
      owner_organization_id: selectedMvo.value !== "all" ? selectedMvo.value : undefined,
      date_from: exportChartStartDate ? toDateParam(exportChartStartDate) : undefined,
      date_to: exportChartEndDate ? toDateParam(exportChartEndDate) : undefined,
    },
    { skip: selectedMvo.value === "all" },
  );

  // Same args as MVODetailedView's request, so the two share one cached result.
  // Period-mode PDFs only carry volumes and recycling, so shifts aren't needed there.
  const needsDriverOnShifts = selectedMvo.value !== "all" && isSingleDay;
  const selectedMvoIds = useMemo(() => [selectedMvo.value], [selectedMvo.value]);
  const {
    shifts: driverOnShifts,
    isLoading: isDriverOnShiftsLoading,
    isFetching: isDriverOnShiftsFetching,
  } = useDriverOnShiftsInRange({
    serviceProviderIds: selectedMvoIds,
    from: exportChartStartDate,
    to: exportChartEndDate,
    skip: !needsDriverOnShifts,
  });

  const serviceProviders = useMemo(() => {
    if (!geoAccessData) return [];

    if (selectedRegion.value === "all") {
      const allProviders = geoAccessData.flatMap((geo) => geo.serviceProviders || []);
      const uniqueProviders = Array.from(new Set(allProviders.map((p) => p.id))).map((id) =>
        allProviders.find((p) => p.id === id),
      );
      return uniqueProviders;
    }

    const region = geoAccessData.find((region) => region.id.toString() === selectedRegion.value);
    return region?.serviceProviders || [];
  }, [geoAccessData, selectedRegion]);
  const mvoOptions = useMemo(
    () =>
      (serviceProviders ?? []).map((provider: any) => ({
        value: provider.id.toString(),
        label: provider.name,
      })),
    [serviceProviders],
  );

  const handleDateUpdate = useCallback((values: any) => {
    const from = values?.range?.from ?? values?.from;
    const to = values?.range?.to ?? values?.to ?? from;
    const nextRange = normalizeDateRange({
      from,
      to,
    });
    if (nextRange.from) setDateRange(nextRange);
  }, []);

  const isPdfDataReady =
    selectedMvo.value !== "all" &&
    !isTruckDataLoading &&
    !isTruckDataFetching &&
    !isProgressSummaryLoading &&
    !isProgressSummaryFetching &&
    !isChartSummaryLoading &&
    !isChartSummaryFetching &&
    !isDriverOnShiftsLoading &&
    !isDriverOnShiftsFetching &&
    truckData != null &&
    progressSummaryData != null &&
    chartSummaryData != null &&
    (!needsDriverOnShifts || driverOnShifts != null);

  const {
    data: allMvosAnomaliesData,
    isLoading: isAllMvosAnomaliesLoading,
    isFetching: isAllMvosAnomaliesFetching,
  } = useGetGovAnalyticsAnomaliesForAllQuery(
    {
      filter: {
        districtOrganizationIds: [],
        serviceProviderIds: [],
      },
      fromTo: {
        from: activeStartDate
          ? { seconds: BigInt(Math.floor(activeStartDate.getTime() / 1000)) }
          : undefined,
        to: activeEndDate
          ? { seconds: BigInt(Math.floor(activeEndDate.getTime() / 1000)) }
          : undefined,
      },
    },
    { skip: selectedMvo.value !== "all" || !activeStartDate || !activeEndDate },
  );

  const {
    data: allMvosDisposalVisitsData,
    isLoading: isAllMvosDisposalVisitsLoading,
    isFetching: isAllMvosDisposalVisitsFetching,
  } = useGetGovAnalyticsDisposalVisitsForAllQuery(
    {
      filter: {
        districtOrganizationIds: [],
        serviceProviderIds: [],
      },
      fromTo: {
        from: activeStartDate
          ? { seconds: BigInt(Math.floor(activeStartDate.getTime() / 1000)) }
          : undefined,
        to: activeEndDate
          ? { seconds: BigInt(Math.floor(activeEndDate.getTime() / 1000)) }
          : undefined,
      },
    },
    { skip: selectedMvo.value !== "all" || !activeStartDate || !activeEndDate },
  );
  const allMvosLandfillsRows = (allMvosDisposalVisitsData?.disposalVisitPerProvider ??
    []) as ConnectProto.GovAnalyticsDisposalVisitPerProvider[];
  const planFactProgressSummaryItems = useMemo(
    () => Object.values(progressSummaryData ?? {}),
    [progressSummaryData],
  );
  const planFactSummaryByOrganization = useMemo(
    () => aggregateSummaryByOrganization(planFactProgressSummaryItems, mvoOptions),
    [planFactProgressSummaryItems, mvoOptions],
  );
  const allMvosAnomalyRows = useMemo(() => {
    const rows = (allMvosAnomaliesData?.anomalyPerProvider ??
      []) as ConnectProto.GovAnalyticsAnomalyPerProvider[];
    return rows.filter((provider) => Number(provider.dailyDisposalFail ?? 0) > 2);
  }, [allMvosAnomaliesData?.anomalyPerProvider]);
  const isAllMvosDataLoading = useMemo(
    () =>
      isAllMvosDisposalVisitsLoading ||
      isAllMvosDisposalVisitsFetching ||
      isProgressSummaryLoading ||
      isProgressSummaryFetching ||
      isAllMvosAnomaliesLoading ||
      isAllMvosAnomaliesFetching,
    [
      isAllMvosAnomaliesFetching,
      isAllMvosAnomaliesLoading,
      isAllMvosDisposalVisitsFetching,
      isAllMvosDisposalVisitsLoading,
      isProgressSummaryFetching,
      isProgressSummaryLoading,
    ],
  );
  const isAllMvosPdfDataReady = useMemo(
    () =>
      selectedMvo.value === "all" &&
      !isAllMvosDataLoading &&
      allMvosDisposalVisitsData != null &&
      progressSummaryData != null &&
      allMvosAnomaliesData != null,
    [
      allMvosAnomaliesData,
      allMvosDisposalVisitsData,
      isAllMvosDataLoading,
      progressSummaryData,
      selectedMvo.value,
    ],
  );
  const isDownloadButtonDisabled =
    selectedMvo.value === "all" ? !isAllMvosPdfDataReady : !isPdfDataReady;
  // The export covers only the visible overview tab, so "is there anything to
  // export" is a question about that tab's rows, not about all three.
  const hasAllMvosExportRows = useMemo(() => {
    switch (allMvosView) {
      case "landfills":
        return allMvosLandfillsRows.length > 0;
      case "plan-fact":
        return planFactSummaryByOrganization.length > 0;
      case "anomalies":
        return allMvosAnomalyRows.length > 0;
      default:
        return false;
    }
  }, [
    allMvosView,
    allMvosAnomalyRows.length,
    allMvosLandfillsRows.length,
    planFactSummaryByOrganization.length,
  ]);
  const totalTrucksForPdf = truckData?.trucks?.length ?? 0;

  const downloadOrShareBlob = useCallback(async (blob: Blob, reportFileName: string) => {
    const shareTitle = "Analytics report";
    const isMobileDevice =
      /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      window.matchMedia("(pointer: coarse)").matches;

    const triggerDownload = (url: string) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = reportFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    let objectUrl: string | null = null;
    try {
      objectUrl = URL.createObjectURL(blob);
      if (isMobileDevice && typeof navigator !== "undefined" && "share" in navigator) {
        const file = new File([blob], reportFileName, { type: "application/pdf" });
        const canShareFiles =
          "canShare" in navigator && navigator.canShare?.({ files: [file] }) === true;

        if (canShareFiles) {
          await navigator.share({
            title: shareTitle,
            files: [file],
          });
          return;
        }

        await navigator.share({
          title: shareTitle,
          text: shareTitle,
          url: objectUrl,
        });
        return;
      }

      triggerDownload(objectUrl);
    } catch (error) {
      console.error("Failed to generate analytics PDF", error);
      if (objectUrl) {
        triggerDownload(objectUrl);
      }
    } finally {
      if (objectUrl) {
        const objectUrlToRevoke = objectUrl;
        setTimeout(() => URL.revokeObjectURL(objectUrlToRevoke), 1000);
      }
    }
  }, []);

  const handleSingleMvoReportAction = useCallback(async () => {
    if (!isPdfDataReady) return;
    const { buildPdfFileName, generateAnalyticsPdfBlob } = await import(
      "../pdf/analytics-pdf.export"
    );
    const reportFileName = buildPdfFileName();
    const blob = await generateAnalyticsPdfBlob({
      progressSummaryData,
      chartSummaryData,
      mvoName: selectedMvo.label,
      mvoId: selectedMvo.value,
      dateMode: isSingleDay ? "daily" : "period",
      rangeStart: exportChartStartDate ?? todayStart,
      rangeEnd: exportChartEndDate ?? todayStart,
      totalTrucks: totalTrucksForPdf,
      driverOnShifts: driverOnShifts ?? [],
      lang,
      t,
    });
    await downloadOrShareBlob(blob, reportFileName);
  }, [
    chartSummaryData,
    downloadOrShareBlob,
    driverOnShifts,
    exportChartEndDate,
    exportChartStartDate,
    isPdfDataReady,
    isSingleDay,
    progressSummaryData,
    totalTrucksForPdf,
    selectedMvo.label,
    todayStart,
    lang,
    t,
  ]);

  const handleAllMvosReportAction = useCallback(async () => {
    if (!isAllMvosPdfDataReady) return;
    if (!hasAllMvosExportRows) {
      toast({
        title:
          t(Namespace.Govdash, "analytics.allMvosAnalytics.noDataForExport") ||
          "Нет данных для экспорта",
        description:
          t(Namespace.Govdash, "analytics.allMvosAnalytics.noDataForExportDescription") ||
          "Для выбранного диапазона дат данные отсутствуют.",
      });
      return;
    }

    const { buildPdfFileName, generateAllMvosAnalyticsPdfBlob } = await import(
      "../pdf/analytics-pdf.export"
    );
    const reportFileName = buildPdfFileName(new Date(), allMvosView);
    const blob = await generateAllMvosAnalyticsPdfBlob({
      disposalVisitPerProvider: allMvosLandfillsRows,
      planFactSummaryByOrganization,
      anomalyPerProvider: allMvosAnomalyRows,
      rangeStart: activeStartDate ?? todayStart,
      rangeEnd: activeEndDate ?? todayStart,
      t,
      lang,
      sectionId: allMvosView,
    });
    await downloadOrShareBlob(blob, reportFileName);
  }, [
    activeEndDate,
    allMvosAnomalyRows,
    allMvosLandfillsRows,
    allMvosView,
    planFactSummaryByOrganization,
    activeStartDate,
    downloadOrShareBlob,
    hasAllMvosExportRows,
    isAllMvosPdfDataReady,
    lang,
    t,
    toast,
    todayStart,
  ]);

  const handleReportAction = useCallback(async () => {
    if (selectedMvo.value === "all") {
      await handleAllMvosReportAction();
      return;
    }
    await handleSingleMvoReportAction();
  }, [handleAllMvosReportAction, handleSingleMvoReportAction, selectedMvo.value]);

  return (
    <div className="flex flex-col items-start gap-2 p-2 h-full relative flex-1">
      {/* Main Container */}
      <div className="bg-white rounded-2xl border border-gray-200 flex flex-col items-start gap-0 w-full">
        {/* Header Section */}
        <div className="flex flex-col items-start w-full gap-0">
          {/* Date Range */}
          <div className="flex items-center justify-between p-4 w-full">
            <h3 className="text-sm font-medium text-gray-900">
              {isSingleDay
                ? `${t(Namespace.Govdash, "wasteCollection")} ${t(Namespace.Govdash, "perDay").toLocaleLowerCase()}`
                : `${t(Namespace.Govdash, "wasteCollection")} ${t(Namespace.Govdash, "forPeriod").toLocaleLowerCase()}`}
            </h3>
            <DateRangePicker
              initialDateFrom={activeStartDate}
              initialDateTo={activeEndDate}
              onUpdate={handleDateUpdate}
              className=""
              disableFutureDates={true}
              showPresets={false}
            />
          </div>

          <Separator className="w-full" />

          {/* Filters */}
          <div className="flex gap-2 items-center justify-center p-4 w-full cursor-pointer">
            {/* MVO Filter */}
            <DropdownSelector
              triggerLabel={selectedMvo.label}
              value={selectedMvo.value}
              onApply={(value) => {
                const selected =
                  value === "all" ? null : (mvoOptions.find((opt) => opt.value === value) ?? null);
                setSelectedMvo({
                  label: selected?.label ?? (t(Namespace.Govdash, "allMvos") || "Все МВО"),
                  value: selected?.value ?? "all",
                });
              }}
              allOptionLabel={t(Namespace.Govdash, "allMvos") || "Все МВО"}
              options={mvoOptions}
              placeholderTitle={t(Namespace.Govdash, "selectMvoPlaceholder") || "Выберите МВО"}
            />

            <Button
              variant="outline"
              type="button"
              disabled={isDownloadButtonDisabled}
              className="h-auto rounded-lg !px-2 !py-1.5 text-[#007AFF] disabled:cursor-not-allowed disabled:bg-[#EAF2FF] disabled:text-[#8FA7C7] disabled:hover:bg-[#EAF2FF]"
              onClick={handleReportAction}
              aria-label={t(Namespace.Govdash, "analytics.downloadPdf") || "Скачать PDF"}
            >
              <IcDownload />
            </Button>
          </div>
        </div>

        {/* Statistics Grid */}
        {selectedMvo.value !== "all" && (
          <>
            <Separator className="w-full" />
            <MVODetailedView
              mvoName={selectedMvo.label}
              mvoId={selectedMvo.value}
              isSingleDay={isSingleDay}
              startDate={activeStartDate}
              endDate={activeEndDate}
              totalTrucks={totalTrucksForPdf}
              selectedRegion={selectedRegion}
              selectedMvo={selectedMvo}
              returnDateFrom={activeStartDate}
              returnDateTo={activeEndDate}
            />
          </>
        )}
      </div>
      {selectedMvo.value === "all" && (
        <AllMvosAnalyticsOverview
          disposalVisitPerProvider={allMvosLandfillsRows}
          planFactSummaryByOrganization={planFactSummaryByOrganization}
          anomalyPerProvider={allMvosAnomalyRows}
          isDisposalOverviewFetching={
            isAllMvosDisposalVisitsLoading || isAllMvosDisposalVisitsFetching
          }
          isPlanFactOverviewFetching={isProgressSummaryLoading || isProgressSummaryFetching}
          isAnomaliesOverviewFetching={isAllMvosAnomaliesLoading || isAllMvosAnomaliesFetching}
          selectedView={allMvosView}
          onSelectedViewChange={setAllMvosView}
        />
      )}
    </div>
  );
}
